import { useEffect, useState } from "react";
import type { MouseEvent } from "react";
import { Upload, Loader2 } from "lucide-react";
import { Form, useRefresh, useTranslate } from "ra-core";
import { Link } from "react-router";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormToolbar } from "@/components/admin/simple-form";
import { FileInput } from "@/components/admin/file-input";
import { FileField } from "@/components/admin/file-field";

import { usePapaParse } from "../misc/usePapaParse";
import type { CompanyImportSchema } from "./useCompanyImport";
import { useCompanyImport } from "./useCompanyImport";
import * as sampleCsv from "./companies_import_sample.csv?raw";

export const CompanyImportButton = () => {
  const translate = useTranslate();
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setModalOpen(true)}
        className="flex items-center gap-2 cursor-pointer"
      >
        <Upload />{" "}
        {translate("resources.companies.import.button", {
          _: "Import CSV",
        })}
      </Button>
      <CompanyImportDialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};

const SAMPLE_URL = `data:text/csv;name=crm_companies_sample.csv;charset=utf-8,${encodeURIComponent(
  sampleCsv.default,
)}`;

type CompanyImportModalProps = {
  open: boolean;
  onClose(): void;
};

export function CompanyImportDialog({
  open,
  onClose,
}: CompanyImportModalProps) {
  const translate = useTranslate();
  const refresh = useRefresh();
  const processBatch = useCompanyImport();
  const { importer, parseCsv, reset } = usePapaParse<CompanyImportSchema>({
    batchSize: 10,
    processBatch,
  });

  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (importer.state === "complete") {
      refresh();
    }
  }, [importer.state, refresh]);

  const startImport = () => {
    if (!file) return;
    parseCsv(file);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleReset = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <Form className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>
              {translate("resources.companies.import.title", {
                _: "Import companies",
              })}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col space-y-2">
            {importer.state === "running" && (
              <div className="flex flex-col gap-2">
                <Alert>
                  <AlertDescription className="flex flex-row gap-4">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {translate("resources.companies.import.running", {
                      _: "The import is running, please do not close this tab.",
                    })}
                  </AlertDescription>
                </Alert>

                <div className="text-sm">
                  {translate("resources.companies.import.progress", {
                    importCount: importer.importCount,
                    rowCount: importer.rowCount,
                    errorCount: importer.errorCount,
                    _: "Imported %{importCount} / %{rowCount} companies, with %{errorCount} errors.",
                  })}
                  {importer.remainingTime !== null && (
                    <>
                      {" "}
                      {translate("resources.companies.import.remaining_time", {
                        _: "Estimated remaining time:",
                      })}{" "}
                      <strong>
                        {millisecondsToTime(importer.remainingTime)}
                      </strong>
                      .{" "}
                      <button
                        type="button"
                        onClick={handleReset}
                        className="text-red-600 underline hover:text-red-800"
                      >
                        {translate("resources.companies.import.stop", {
                          _: "Stop import",
                        })}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {importer.state === "error" && (
              <Alert variant="destructive">
                <AlertDescription>
                  {translate("resources.companies.import.error", {
                    _: "Failed to import this file, please make sure you provided a valid CSV file.",
                  })}
                </AlertDescription>
              </Alert>
            )}

            {importer.state === "complete" && (
              <Alert>
                <AlertDescription>
                  {translate("resources.companies.import.complete", {
                    importCount: importer.importCount,
                    errorCount: importer.errorCount,
                    _: "Companies import complete. Processed %{importCount} rows, with %{errorCount} errors.",
                  })}
                </AlertDescription>
              </Alert>
            )}

            {importer.state === "idle" && (
              <>
                <Alert>
                  <AlertDescription className="flex flex-col gap-4">
                    {translate("resources.companies.import.sample_hint", {
                      _: "Use the sample CSV as a column template. Existing companies are updated by Tax ID or exact name; new companies are created.",
                    })}
                    <Button asChild variant="outline" size="sm">
                      <Link
                        to={SAMPLE_URL}
                        download={"crm_companies_sample.csv"}
                      >
                        {translate(
                          "resources.companies.import.sample_download",
                          {
                            _: "Download CSV sample",
                          },
                        )}
                      </Link>
                    </Button>{" "}
                  </AlertDescription>
                </Alert>

                <FileInput
                  source="csv"
                  label="resources.companies.import.csv_file"
                  accept={{ "text/csv": [".csv"] }}
                  onChange={setFile}
                >
                  <FileField source="src" title="title" target="_blank" />
                </FileInput>
              </>
            )}
          </div>
        </Form>

        <div className="flex justify-start pt-6">
          <FormToolbar>
            {importer.state === "idle" ? (
              <Button onClick={startImport} disabled={!file}>
                {translate("resources.companies.import.button", {
                  _: "Import CSV",
                })}
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={importer.state === "running"}
              >
                {translate("ra.action.close")}
              </Button>
            )}
          </FormToolbar>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function millisecondsToTime(ms: number) {
  const seconds = Math.floor((ms / 1000) % 60);
  const minutes = Math.floor((ms / (60 * 1000)) % 60);

  return `${minutes}m ${seconds}s`;
}
